import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';
import BackgroundIcons from '../components/BackgroundIcons';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weddingDate, setWeddingDate] = useState(null);
  const [savedThemes, setSavedThemes] = useState([]);
  const [shortlists, setShortlists] = useState([]);
  const [invitationCount, setInvitationCount] = useState(0);
  const [ritualCount, setRitualCount] = useState(0);
  const [timelineCount, setTimelineCount] = useState(0);
  const [recentActivity, setRecentActivity] = useState([]);
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel with individual error handling
      const results = await Promise.allSettled([
        apiService.users.getProfile(),
        apiService.savedThemes.getAll({ limit: 1000 }),
        fetch(`${API_ENDPOINTS.vendors}/shortlist`, {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }),
        apiService.invitations.getMyInvitations(),
        apiService.userRituals.getAll(),
        apiService.userTimelines.getAll()
      ]);

      // Handle user profile
      if (results[0].status === 'fulfilled' && results[0].value.data.success) {
        setUser(results[0].value.data.user);
        setWeddingDate(results[0].value.data.user.wedding_date);
      }

      // Handle saved themes
      if (results[1].status === 'fulfilled' && results[1].value.data.success) {
        setSavedThemes(results[1].value.data.themes || []);
      } else {
        setSavedThemes([]);
      }

      // Handle shortlisted vendors
      if (results[2].status === 'fulfilled' && results[2].value.ok) {
        const shortlistData = await results[2].value.json();
        setShortlists(shortlistData.shortlists || []);
      } else {
        setShortlists([]);
      }

      // Handle invitation count
      if (results[3].status === 'fulfilled' && results[3].value.data.success) {
        setInvitationCount(results[3].value.data.count || 0);
      } else {
        setInvitationCount(0);
      }

      // Handle ritual count
      if (results[4].status === 'fulfilled' && results[4].value.success) {
        setRitualCount(results[4].value.rituals?.length || 0);
      } else {
        setRitualCount(0);
      }

      // Handle timeline count
      if (results[5].status === 'fulfilled') {
        const timelineData = results[5].value.data || results[5].value;
        console.log('Timeline API response:', timelineData);
        if (timelineData.success) {
          setTimelineCount(timelineData.timelines?.length || 0);
        } else {
          setTimelineCount(0);
        }
      } else {
        console.error('Timeline API failed:', results[5]);
        setTimelineCount(0);
      }

      // TODO: Recent Activity - will use real tracked data in future
      // generateRecentActivity();

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateRecentActivity = () => {
    const activities = [
      { type: 'theme', action: 'Generated new wedding theme', time: '2 hours ago', icon: '🎨' },
      { type: 'vendor', action: 'Shortlisted photographer', time: '1 day ago', icon: '📸' },
      { type: 'budget', action: 'Updated budget allocation', time: '2 days ago', icon: '💰' },
      { type: 'timeline', action: 'Created wedding timeline', time: '3 days ago', icon: '📅' }
    ];
    setRecentActivity(activities);
  };

  const calculateDaysUntilWedding = () => {
    if (!weddingDate) return null;
    const today = new Date();
    const wedding = new Date(weddingDate);
    const diffTime = wedding - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const calculateOverallProgress = () => {
    const totalSections = 8;
    let completedSections = 0;
    
    if (savedThemes.length > 0) completedSections++;
    if (shortlists.length > 0) completedSections++;
    if (weddingDate) completedSections++;
    // Add more conditions as features are implemented
    
    return Math.round((completedSections / totalSections) * 100);
  };

  const handleSetWeddingDate = async () => {
    if (!selectedDate) return;
    
    try {
      const response = await apiService.users.updateProfile({
        wedding_date: selectedDate
      });
      
      if (response.data.success) {
        setWeddingDate(selectedDate);
        setUser(prevUser => ({ ...prevUser, wedding_date: selectedDate }));
        setShowDateModal(false);
        setSelectedDate('');
      }
    } catch (error) {
      console.error('Error updating wedding date:', error);
    }
  };

  const dashboardSections = [
    {
      id: 'dream-vision',
      title: 'Dream & Vision',
      description: 'Wedding themes & inspiration',
      icon: '🎨',
      count: savedThemes.length,
      color: 'from-rose-400 to-pink-500',
      link: savedThemes.length > 0 ? '/dashboard/themes' : '/wedding-blueprint/dream-wedding-generator',
      stats: `${savedThemes.length} themes saved`
    },
    {
      id: 'vendors',
      title: 'Vendors',
      description: 'Shortlisted wedding vendors',
      icon: '🏪',
      count: shortlists.length,
      color: 'from-purple-400 to-indigo-500',
      link: shortlists.length > 0 ? '/dashboard/vendors' : '/vendor-vault/smart-vendor-matching',
      stats: `${shortlists.length} vendors shortlisted`
    },
    {
      id: 'budget',
      title: 'Budget',
      description: 'Financial planning & tracking',
      icon: '💰',
      count: 0,
      color: 'from-green-400 to-emerald-500',
      link: '/dashboard/budget',
      stats: 'Budget planning'
    },
    {
      id: 'timeline',
      title: 'Timeline & Tasks',
      description: 'Wedding planning schedule',
      icon: '📅',
      count: timelineCount,
      color: 'from-blue-400 to-cyan-500',
      link: '/dashboard/timeline',
      stats: `${timelineCount} saved`
    },
    {
      id: 'cultural',
      title: 'Cultural & Traditions',
      description: 'Rituals & ceremonies',
      icon: '🎭',
      count: ritualCount,
      color: 'from-orange-400 to-red-500',
      link: '/dashboard/rituals',
      stats: `${ritualCount} saved`
    },
    {
      id: 'invitations',
      title: 'Invitations',
      description: 'Invites & communications',
      icon: '📝',
      count: invitationCount,
      color: 'from-teal-400 to-green-500',
      link: '/dashboard/invitations',
      stats: `${invitationCount} saved`
    },
    {
      id: 'memories',
      title: 'Memories',
      description: 'Memory capsules & photos',
      icon: '📸',
      count: 0,
      color: 'from-pink-400 to-rose-500',
      link: '/ever-after-memories/memory-capsule-creator',
      stats: 'Memory collection'
    },
    {
      id: 'weather',
      title: 'Weather & Logistics',
      description: 'Weather planning & logistics',
      icon: '🌤️',
      count: 0,
      color: 'from-yellow-400 to-orange-500',
      link: '/big-day-playbook/weather-risk-advisor',
      stats: 'Weather insights'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your wedding dashboard...</p>
        </div>
      </div>
    );
  }

  const daysUntilWedding = calculateDaysUntilWedding();
  const overallProgress = calculateOverallProgress();

  const dashboardIcons = [
    { emoji: '💍', size: '4xl', opacity: '15' },
    { emoji: '💖', size: '4xl', opacity: '15' },
    { emoji: '✨', size: '4xl', opacity: '15' },
    { emoji: '🎯', size: '4xl', opacity: '15' },
    { emoji: '🌸', size: '4xl', opacity: '15' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={dashboardIcons} />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        
        {/* TOP: Wedding Countdown + Progress Overview */}
        <div className="mb-16">
          {/* Header */}
          <div className="text-center mb-12 relative">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
              <span className="text-4xl animate-pulse">💍</span>
            </div>
            
            <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6 tracking-wide animate-fadeInUp">
              Welcome back, {user?.name?.split(' ')[0] || 'Beautiful'}! 
            </h1>
            
            <div className="flex items-center justify-center mb-6">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="mx-4 text-3xl animate-bounce">💖</div>
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            </div>
            
            <p className="text-2xl text-gray-700 font-light italic animate-fadeInUp delay-300">
              Your dream wedding planning journey ✨
            </p>
          </div>

          {/* Countdown & Progress Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            
            {/* Wedding Countdown */}
            <div className="group relative bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-rose-200/50 text-center hover:shadow-3xl transform hover:scale-105 transition-all duration-500 overflow-hidden">
              {/* Card decorations */}
              <div className="absolute top-4 right-4 text-rose-300 animate-pulse">💖</div>
              <div className="absolute bottom-4 left-4 text-gold-300 animate-pulse delay-1000">✨</div>
              
              <div className="relative z-10">
                <div className="w-24 h-24 bg-gradient-to-r from-rose-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 transition-transform duration-300">
                  <span className="text-4xl group-hover:animate-bounce">💍</span>
                </div>
                
                <h3 className="text-3xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent mb-4">
                  Wedding Countdown
                </h3>
                
                {daysUntilWedding !== null ? (
                  <>
                    <div className="text-6xl font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent mb-3 animate-pulse">
                      {daysUntilWedding}
                    </div>
                    <p className="text-xl text-gray-700 font-medium">
                      {daysUntilWedding === 0 ? 'Today is the day! 🎉' : 
                       daysUntilWedding === 1 ? 'day to go! 💖' : 'days to go! ✨'}
                    </p>
                  </>
                ) : (
                  <div className="text-gray-600">
                    <p className="text-lg mb-6">Set your wedding date to see countdown</p>
                    <button 
                      onClick={() => setShowDateModal(true)}
                      className="px-8 py-3 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-full font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 animate-pulse"
                    >
                      <span className="mr-2">💕</span>
                      Set Your Special Date
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Overall Progress */}
            <div className="group relative bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-purple-200/50 text-center hover:shadow-3xl transform hover:scale-105 transition-all duration-500 overflow-hidden">
              {/* Card decorations */}
              <div className="absolute top-4 right-4 text-purple-300 animate-pulse">🌟</div>
              <div className="absolute bottom-4 left-4 text-indigo-300 animate-pulse delay-1000">💫</div>
              
              <div className="relative z-10">
                <div className="w-24 h-24 bg-gradient-to-r from-purple-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 transition-transform duration-300">
                  <span className="text-4xl group-hover:animate-bounce">📊</span>
                </div>
                
                <h3 className="text-3xl font-serif font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-6">
                  Planning Progress
                </h3>
                
                {/* Progress Ring */}
                <div className="relative w-40 h-40 mx-auto mb-6">
                  <svg className="w-40 h-40 transform -rotate-90" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="50" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                    <circle 
                      cx="60" 
                      cy="60" 
                      r="50" 
                      stroke="url(#progressGradient)" 
                      strokeWidth="8" 
                      fill="none"
                      strokeDasharray={`${overallProgress * 3.14} 314`}
                      strokeLinecap="round"
                      className="transition-all duration-2000 animate-pulse"
                    />
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#8b5cf6" />
                        <stop offset="50%" stopColor="#ec4899" />
                        <stop offset="100%" stopColor="#f59e0b" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      {overallProgress}%
                    </span>
                  </div>
                </div>
                
                <p className="text-xl text-gray-700 font-medium">Wedding planning complete</p>
              </div>
            </div>
          </div>
        </div>

        {/* MIDDLE: Quick Access Cards */}
        <div className="mb-16">
          <div className="text-center mb-12 relative">
            <h2 className="text-5xl font-serif font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-4 animate-fadeInUp">
              Your Wedding Planning Hub
            </h2>
            
            <div className="flex items-center justify-center mb-6">
              <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full"></div>
              <div className="mx-4 text-2xl animate-pulse">💖</div>
              <div className="w-24 h-1 bg-gradient-to-r from-pink-500 to-gold-400 rounded-full"></div>
            </div>
            
            <p className="text-xl text-gray-700 font-light italic animate-fadeInUp delay-300">
              Manage every magical moment of your dream wedding ✨
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {dashboardSections.map((section, index) => (
              <a
                key={section.id}
                href={section.link}
                className="group relative bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-xl border-2 border-white/50 hover:shadow-3xl transform hover:scale-110 transition-all duration-500 overflow-hidden animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Card hover decorations */}
                <div className="absolute top-3 right-3 text-gold-300 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-300">✨</div>
                <div className="absolute bottom-3 left-3 text-rose-300 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ animationDelay: '0.5s' }}>💫</div>
                
                {/* Content */}
                <div className="relative z-10">
                  {/* Icon & Count */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-20 h-20 bg-gradient-to-r ${section.color} rounded-2xl flex items-center justify-center shadow-xl group-hover:scale-125 group-hover:rotate-12 transition-all duration-500`}>
                      <span className="text-3xl group-hover:animate-bounce">{section.icon}</span>
                    </div>
                    {section.count > 0 && (
                      <div className={`px-4 py-2 bg-gradient-to-r ${section.color} text-white rounded-full text-sm font-bold shadow-lg animate-pulse`}>
                        {section.count}
                      </div>
                    )}
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-2xl font-serif font-bold text-gray-800 mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 group-hover:bg-clip-text transition-all duration-300">
                    {section.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 group-hover:text-gray-700 transition-colors duration-300">
                    {section.description}
                  </p>
                  
                  {/* Stats */}
                  <div className="text-xs text-gray-500 font-medium group-hover:text-gray-600 transition-colors duration-300">
                    {section.stats}
                  </div>
                </div>

                {/* Hover arrow */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                  <span className="text-2xl group-hover:animate-bounce">→</span>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* TODO: Recent Activity Feed - will be implemented in future with real tracked activity data */}
      </div>

      {/* Wedding Date Modal */}
      {showDateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-rose-200/50 max-w-md w-full p-10 overflow-hidden animate-fadeInScale">
            <div className="relative z-10">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-r from-rose-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl animate-bounce">
                  <span className="text-3xl animate-pulse">💍</span>
                </div>
                <h3 className="text-3xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent mb-3">
                  Set Your Wedding Date
                </h3>
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-1 bg-gradient-to-r from-rose-400 to-pink-500 rounded-full"></div>
                  <div className="mx-3 text-xl animate-bounce">💖</div>
                  <div className="w-16 h-1 bg-gradient-to-r from-pink-500 to-gold-400 rounded-full"></div>
                </div>
                <p className="text-lg text-gray-600 font-light italic">When is your magical day?</p>
              </div>

              <div className="mb-8">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-6 py-4 border-2 border-rose-200 rounded-2xl focus:border-rose-400 focus:outline-none text-lg text-center font-semibold bg-gradient-to-r from-white to-rose-50 shadow-lg transition-all duration-300 hover:shadow-xl"
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowDateModal(false);
                    setSelectedDate('');
                  }}
                  className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-2xl font-bold hover:bg-gray-50 hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetWeddingDate}
                  disabled={!selectedDate}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-2xl font-bold hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <span className="mr-2">💕</span>
                  Save Date
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
